import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant, Button } from '@mprolab/uikit'
import MainWrapper from 'layouts/MainWrapper'
import { StyledBiennale } from 'components'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useLifetimeTokens } from 'state/global/hooks'
import {
  useCreateNodeStorageShareMutation,
  useDeleteNodeStorageFileMutation,
  useGetLicensesQuery,
  useGetNodeStorageListQuery,
  useGetNodeStorageSharesQuery,
  useGetNodeStorageSummaryQuery,
  useMkdirNodeStorageFolderMutation,
  useMoveNodeStorageItemMutation,
  useRevokeNodeStorageShareMutation,
  useRotateNodeStorageShareTokenMutation,
  useUpdateNodeStorageShareMutation,
  useUploadNodeStorageFileMutation,
  type NodeStorageSharePermission
} from 'state/services/indexNode/indexNode'
import isNil from 'lodash/isNil'
import { LicenseStatus, LicenseType, SessionType } from '@mprolab/sdk'
import { useTranslation } from 'react-i18next'
import useToast from 'hooks/useToast'
import { useIsUserVerified } from 'state/user/hooks'
import AppSessionRequiredPanel from 'components/AppSessionRequiredPanel'
import { useVerifyIndexNode } from 'hooks/useVerifyIndexNode'
import { useAwaitingWalletVerificationModal } from 'components/widgets/VerificationModals'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'state'
import { setLifetimeTokens } from 'state/global/actions'
import { indexNodeStore } from 'mobxStores/IndexNodeStore'
import { downloadNodeStorageFile } from 'utils/downloadNodeStorageFile'
import { uploadNodeStorageFileChunked } from 'utils/uploadNodeStorageChunked'
import { buildPublicNodeStorageShareUrl } from 'utils/publicNodeStorageShare'
import { Folder, File } from 'react-feather'

const StyledTitle = styled(StyledBiennale)`
  font-size: 32px;
`

const TableShell = styled(Flex)`
  gap: 0;
  width: 100%;
  min-height: 320px;
  padding: 0;
  flex-direction: column;
  justify-content: flex-start;
  background: ${({ theme }) => theme.colors.backgroundContrast};
  margin: 16px 0 56px;
  border-radius: 12px;
  overflow: hidden;
  ${({ theme }) => theme.mediaQueries.mobile} {
    margin-bottom: 32px;
  }
`

const Workspace = styled(Flex)`
  width: 100%;
  flex-direction: row;
  align-items: stretch;
  min-height: 360px;
  gap: 0;
  ${({ theme }) => theme.mediaQueries.mobile} {
    flex-direction: column;
  }
`

const SidebarPanel = styled(Flex)`
  flex-direction: column;
  width: 280px;
  flex-shrink: 0;
  border-right: 1px solid ${({ theme }) => theme.colors.gray8};
  padding: 16px 12px;
  gap: 10px;
  background: ${({ theme }) => theme.colors.background};
  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray8};
  }
`

const LicenseRow = styled.button<{ $active: boolean }>`
  text-align: left;
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.cyan : theme.colors.gray8)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.backgroundContrast : theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  font-size: 13px;
  line-height: 1.35;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const MainPanel = styled(Flex)`
  flex: 1;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  padding: 16px 16px 20px 20px;
  ${({ theme }) => theme.mediaQueries.mobile} {
    padding: 16px 12px 20px;
  }
`

const DropSurface = styled(Flex)<{ $active: boolean }>`
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 220px;
  border-radius: 12px;
  border: 2px dashed
    ${({ theme, $active }) => ($active ? theme.colors.cyan : theme.colors.gray8)};
  background: ${({ theme, $active }) => ($active ? `${theme.colors.cyan}18` : 'transparent')};
  padding: 14px;
  transition: border-color 0.15s ease, background 0.15s ease;
`

const Toolbar = styled(Flex)`
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`

const QuotaBar = styled.div<{ pct: number }>`
  height: 6px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.gray8};
  width: 100%;
  overflow: hidden;
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ pct }) => `${Math.min(100, Math.max(0, pct))}%`};
    background: ${({ theme }) => theme.colors.cyan};
  }
`

const Crumb = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.cyan};
  font-size: 13px;
  &:hover {
    text-decoration: underline;
  }
`

const RowGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(96px, 128px) 72px auto;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
`

const ListHeaderGrid = styled(RowGrid)`
  background: ${({ theme }) => theme.colors.background};
  opacity: 0.85;
  padding-top: 8px;
  padding-bottom: 8px;
`

const ShareSection = styled(Flex)`
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
  padding: 14px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
`

const ShareRow = styled(Flex)`
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray8};
  &:last-child {
    border-bottom: none;
  }
`

const Select = styled.select`
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function joinPath(base: string, segment: string): string {
  if (!base) return segment
  return `${base.replace(/\/$/, '')}/${segment}`
}

function sanitizeNewSegment(raw: string): string | null {
  const s = raw.trim()
  if (!s) return null
  if (s.includes('/') || s.includes('\\')) return null
  if (s === '.' || s === '..') return null
  if (s.startsWith('.')) return null
  return s
}

function getErrorStatus(err: unknown): number | undefined {
  if (err && typeof err === 'object' && 'status' in err) {
    const s = (err as { status: unknown }).status
    return typeof s === 'number' ? s : undefined
  }
  return undefined
}

const NodesStorage: React.FC = () => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { toastError, toastSuccess } = useToast()
  const { account, provider } = useWeb3React()
  const isAppVerified = useIsUserVerified()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = account ? lifetimeTokens?.[account] : undefined
  const dispatch = useDispatch<AppDispatch>()
  const verifyIndexNode = useVerifyIndexNode()
  const { onPresentAwaitingVerificationModal, onModalClose } = useAwaitingWalletVerificationModal({})

  const [licenseId, setLicenseId] = useState<string>('')
  const [folderPath, setFolderPath] = useState('')
  const [newSharePermission, setNewSharePermission] =
    useState<NodeStorageSharePermission>('read')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragDepthRef = useRef(0)
  const [isDragging, setIsDragging] = useState(false)

  const {
    data: licenses,
    isLoading: licensesLoading,
    error: licensesError
  } = useGetLicensesQuery(
    { account: account!, accessToken: accessToken! },
    { skip: isNil(accessToken) || !account || !isAppVerified }
  )

  const activeLicenses = useMemo(
    () =>
      (licenses ?? []).filter(
        (l) => l.status !== LicenseStatus.TERMINATED && l.status !== LicenseStatus.PAUSED
      ),
    [licenses]
  )

  useEffect(() => {
    if (!licenseId && activeLicenses.length > 0) {
      setLicenseId(activeLicenses[0].licenseId)
    }
  }, [activeLicenses, licenseId])

  const verifyAndSetToken = async () => {
    if (!account || !provider) return
    indexNodeStore.setIsLoading(true)
    onPresentAwaitingVerificationModal()
    await verifyIndexNode({
      account,
      provider,
      successCallback: async (token) => {
        dispatch(setLifetimeTokens({ account, accessToken: token }))
        onModalClose()
        indexNodeStore.setIsLoading(false)
      },
      errorCallback: async () => {
        toastError(t('protectedPages.indexNodeSessionFailed'))
        onModalClose()
        indexNodeStore.setIsLoading(false)
      },
      sessionType: SessionType.LIFETIME
    })
  }

  useEffect(() => {
    if (!account || !isAppVerified) return
    if (isNil(accessToken) || licensesError) {
      void verifyAndSetToken()
    }
  }, [account, isAppVerified, accessToken, licensesError])

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary
  } = useGetNodeStorageSummaryQuery(
    { account: account!, accessToken: accessToken!, licenseId },
    { skip: !licenseId || isNil(accessToken) || !account }
  )

  const {
    data: listing,
    isLoading: listLoading,
    refetch: refetchList
  } = useGetNodeStorageListQuery(
    { account: account!, accessToken: accessToken!, licenseId, path: folderPath || undefined },
    { skip: !licenseId || isNil(accessToken) || !account }
  )

  const [uploadFile, { isLoading: uploading }] = useUploadNodeStorageFileMutation()
  const [deleteFile, { isLoading: deleting }] = useDeleteNodeStorageFileMutation()
  const [mkdirFolder, { isLoading: mkdirBusy }] = useMkdirNodeStorageFolderMutation()
  const [moveItem, { isLoading: moveBusy }] = useMoveNodeStorageItemMutation()
  const shareQuerySkip =
    !licenseId || isNil(accessToken) || !account || !summary || Boolean(summaryError)
  const { data: shareList, refetch: refetchShares } = useGetNodeStorageSharesQuery(
    { account: account!, accessToken: accessToken!, licenseId },
    { skip: shareQuerySkip }
  )
  const [createShare, { isLoading: creatingShare }] = useCreateNodeStorageShareMutation()
  const [updateSharePerm] = useUpdateNodeStorageShareMutation()
  const [rotateShareToken] = useRotateNodeStorageShareTokenMutation()
  const [revokeShare] = useRevokeNodeStorageShareMutation()

  const pct = summary ? (summary.usedBytes / Math.max(1, summary.quotaBytes)) * 100 : 0

  const licenseLabel = useCallback(
    (l: { licenseId: string; type: LicenseType; name?: string | null }) => {
      const typeLabel =
        l.type === LicenseType.MGA_INODE
          ? t('general.iNode')
          : l.type === LicenseType.MGA_LIGHT
            ? t('general.ggNode')
            : t('general.mga')
      const name = l.name?.trim()
      return `${typeLabel}${name ? ` — ${name}` : ''}`
    },
    [t]
  )

  const licenseSubtitle = useCallback((l: { licenseId: string }) => `${l.licenseId.slice(0, 8)}…`, [])

  const uploadFiles = async (files: File[]) => {
    if (!account || !accessToken || !licenseId || !summary) return
    const maxSingle = summary.chunkUpload?.maxChunkBytes ?? 4_194_304
    for (const file of files) {
      const relativePath = folderPath ? joinPath(folderPath, file.name) : file.name
      try {
        if (file.size <= maxSingle) {
          await uploadFile({ account, accessToken, licenseId, file, relativePath }).unwrap()
        } else {
          await uploadNodeStorageFileChunked({
            account,
            accessToken,
            licenseId,
            file,
            relativePath,
            summary
          })
        }
      } catch {
        toastError(t('nodesStorage.uploadFailed'))
        return
      }
    }
    toastSuccess(t('nodesStorage.uploadDone'))
    void refetchList()
    void refetchSummary()
  }

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files
    e.target.value = ''
    if (!list?.length) return
    await uploadFiles(Array.from(list))
  }

  const onDownload = async (rel: string, name: string) => {
    if (!account || !accessToken) return
    try {
      await downloadNodeStorageFile({
        account,
        accessToken,
        licenseId,
        path: rel,
        filenameHint: name
      })
    } catch {
      toastError(t('nodesStorage.downloadFailed'))
    }
  }

  const onDelete = async (rel: string) => {
    if (!account || !accessToken) return
    if (!window.confirm(t('nodesStorage.confirmDelete'))) return
    try {
      await deleteFile({ account, accessToken, licenseId, path: rel }).unwrap()
      toastSuccess(t('nodesStorage.deleteDone'))
      void refetchList()
      void refetchSummary()
    } catch {
      toastError(t('nodesStorage.deleteFailed'))
    }
  }

  const onNewFolder = async () => {
    if (!account || !accessToken || !licenseId) return
    const raw = window.prompt(t('nodesStorage.newFolderPrompt'))
    if (raw == null) return
    const seg = sanitizeNewSegment(raw)
    if (!seg) {
      toastError(t('nodesStorage.invalidName'))
      return
    }
    const target = folderPath ? joinPath(folderPath, seg) : seg
    try {
      await mkdirFolder({ account, accessToken, licenseId, path: target }).unwrap()
      toastSuccess(t('nodesStorage.folderCreated'))
      void refetchList()
    } catch (e: unknown) {
      if (getErrorStatus(e) === 409) toastError(t('nodesStorage.conflictName'))
      else toastError(t('nodesStorage.mkdirFailed'))
    }
  }

  const onRename = async (entName: string) => {
    if (!account || !accessToken || !licenseId) return
    const fromRel = folderPath ? joinPath(folderPath, entName) : entName
    const raw = window.prompt(t('nodesStorage.renamePrompt'), entName)
    if (raw == null) return
    const seg = sanitizeNewSegment(raw)
    if (!seg) {
      toastError(t('nodesStorage.invalidName'))
      return
    }
    const toRel = folderPath ? joinPath(folderPath, seg) : seg
    if (fromRel === toRel) return
    try {
      await moveItem({ account, accessToken, licenseId, from: fromRel, to: toRel }).unwrap()
      toastSuccess(t('nodesStorage.renameDone'))
      void refetchList()
    } catch (e: unknown) {
      if (getErrorStatus(e) === 409) toastError(t('nodesStorage.conflictName'))
      else toastError(t('nodesStorage.renameFailed'))
    }
  }

  const summaryErrMsg =
    summaryError && typeof summaryError === 'object' && 'status' in summaryError && summaryError.status === 503
      ? t('nodesStorage.notConfigured')
      : summaryError
        ? t('nodesStorage.loadFailed')
        : null

  const onCreatePublicShare = async () => {
    if (!account || !accessToken || !licenseId) return
    try {
      const res = await createShare({
        account,
        accessToken,
        licenseId,
        folderPath: folderPath || '',
        permission: newSharePermission
      }).unwrap()
      const url = buildPublicNodeStorageShareUrl(res.token)
      try {
        await navigator.clipboard.writeText(url)
        toastSuccess(t('nodesStorage.sharing.createdAndCopied'))
      } catch {
        toastSuccess(t('nodesStorage.sharing.createdShowToken'))
        window.prompt(t('nodesStorage.sharing.saveLink'), url)
      }
      void refetchShares()
    } catch {
      toastError(t('nodesStorage.sharing.createFailed'))
    }
  }

  const onChangeSharePermission = async (
    shareId: string,
    permission: NodeStorageSharePermission
  ) => {
    if (!account || !accessToken || !licenseId) return
    try {
      await updateSharePerm({ account, accessToken, licenseId, shareId, permission }).unwrap()
      toastSuccess(t('nodesStorage.sharing.permissionUpdated'))
      void refetchShares()
    } catch {
      toastError(t('nodesStorage.sharing.updateFailed'))
    }
  }

  const onRotateShare = async (shareId: string) => {
    if (!account || !accessToken || !licenseId) return
    if (!window.confirm(t('nodesStorage.sharing.confirmRotate'))) return
    try {
      const { token } = await rotateShareToken({ account, accessToken, licenseId, shareId }).unwrap()
      const url = buildPublicNodeStorageShareUrl(token)
      try {
        await navigator.clipboard.writeText(url)
        toastSuccess(t('nodesStorage.sharing.rotatedAndCopied'))
      } catch {
        window.prompt(t('nodesStorage.sharing.saveLink'), url)
      }
      void refetchShares()
    } catch {
      toastError(t('nodesStorage.sharing.rotateFailed'))
    }
  }

  const onRevokeShare = async (shareId: string) => {
    if (!account || !accessToken || !licenseId) return
    if (!window.confirm(t('nodesStorage.sharing.confirmRevoke'))) return
    try {
      await revokeShare({ account, accessToken, licenseId, shareId }).unwrap()
      toastSuccess(t('nodesStorage.sharing.revoked'))
      void refetchShares()
    } catch {
      toastError(t('nodesStorage.sharing.revokeFailed'))
    }
  }

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragDepthRef.current += 1
    setIsDragging(true)
  }

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragDepthRef.current -= 1
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0
      setIsDragging(false)
    }
  }

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragDepthRef.current = 0
    setIsDragging(false)
    if (!summary || uploading) return
    const fl = e.dataTransfer.files
    if (!fl?.length) return
    await uploadFiles(Array.from(fl))
  }

  const breadcrumbParts = folderPath ? folderPath.split('/').filter(Boolean) : []

  return (
    <MainWrapper>
      <Flex fullWidth mt="80px" mb="120px" flexDirection="column">
        <Flex mt="72px" mb="24px" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="16px">
          <StyledTitle>{t('nodesStorage.title')}</StyledTitle>
        </Flex>
        <Text variant={TextVariant.BODY_DEFAULT} color={colors.gray6} mb="16px" maxWidth="720px">
          {t('nodesStorage.subtitle')}
        </Text>
        <TableShell>
          {account && !isAppVerified ? (
            <Flex p="24px">
              <AppSessionRequiredPanel />
            </Flex>
          ) : (
            <Workspace>
              <SidebarPanel>
                <Text variant={TextVariant.BODY_SMALL_BOLD} color={colors.gray6}>
                  {t('nodesStorage.myLicenses')}
                </Text>
                {licensesLoading ? (
                  <Text variant={TextVariant.BODY_DEFAULT}>{t('nodesStorage.loadingLicenses')}</Text>
                ) : activeLicenses.length === 0 ? (
                  <Text variant={TextVariant.BODY_DEFAULT}>{t('nodesStorage.noLicenses')}</Text>
                ) : (
                  activeLicenses.map((l) => (
                    <LicenseRow
                      key={l.licenseId}
                      type="button"
                      $active={licenseId === l.licenseId}
                      onClick={() => {
                        setLicenseId(l.licenseId)
                        setFolderPath('')
                      }}
                    >
                      <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{licenseLabel(l)}</Text>
                      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6} mt="4px">
                        {licenseSubtitle(l)}
                      </Text>
                      {licenseId === l.licenseId && summary && !summaryErrMsg ? (
                        <Flex flexDirection="column" gap="6px" mt="10px">
                          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                            {t('nodesStorage.quota', {
                              used: formatBytes(summary.usedBytes),
                              total: formatBytes(summary.quotaBytes)
                            })}
                          </Text>
                          <QuotaBar pct={pct} />
                        </Flex>
                      ) : null}
                    </LicenseRow>
                  ))
                )}
              </SidebarPanel>

              <MainPanel>
                {activeLicenses.length === 0 ? null : summaryErrMsg ? (
                  <Text variant={TextVariant.BODY_DEFAULT} color={colors.magenta}>
                    {summaryErrMsg}
                  </Text>
                ) : summaryLoading ? (
                  <Text variant={TextVariant.BODY_DEFAULT}>{t('nodesStorage.loadingQuota')}</Text>
                ) : (
                  <DropSurface
                    $active={isDragging}
                    onDragEnter={onDragEnter}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={(e) => void onDrop(e)}
                  >
                    {isDragging ? (
                      <Text variant={TextVariant.BODY_DEFAULT_BOLD} color={colors.cyan} textAlign="center">
                        {t('nodesStorage.dropFilesHere')}
                      </Text>
                    ) : null}
                    <Toolbar>
                      <Flex gap="8px" alignItems="center" flexWrap="wrap" flex="1" minWidth="0">
                        <Crumb type="button" onClick={() => setFolderPath('')}>
                          {t('nodesStorage.allFiles')}
                        </Crumb>
                        {breadcrumbParts.map((seg, i) => {
                          const pathUpTo = breadcrumbParts.slice(0, i + 1).join('/')
                          return (
                            <Flex key={pathUpTo} gap="6px" alignItems="center">
                              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                                /
                              </Text>
                              <Crumb type="button" onClick={() => setFolderPath(pathUpTo)}>
                                {seg}
                              </Crumb>
                            </Flex>
                          )
                        })}
                      </Flex>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={!summary || mkdirBusy || moveBusy || Boolean(summaryErrMsg)}
                        onClick={() => void onNewFolder()}
                      >
                        {t('nodesStorage.newFolder')}
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        hidden
                        onChange={onPickFile}
                        disabled={uploading || !summary || Boolean(summaryErrMsg)}
                      />
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        disabled={uploading || !summary || Boolean(summaryErrMsg)}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {uploading ? t('nodesStorage.uploading') : t('nodesStorage.upload')}
                      </Button>
                    </Toolbar>

                    {summary ? (
                      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray8}>
                        {t('nodesStorage.volumePath', { path: summary.volumeRelativePath })}
                      </Text>
                    ) : null}

                    {summary && !summaryErrMsg ? (
                      <ShareSection>
                        <Text variant={TextVariant.BODY_SMALL_BOLD} color={colors.gray6}>
                          {t('nodesStorage.sharing.title')}
                        </Text>
                        <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray8}>
                          {t('nodesStorage.sharing.hint')}
                        </Text>
                        <Flex flexWrap="wrap" gap="10px" alignItems="center">
                          <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                            {t('nodesStorage.sharing.newLinkPermission')}
                          </Text>
                          <Select
                            value={newSharePermission}
                            onChange={(e) =>
                              setNewSharePermission(e.target.value as NodeStorageSharePermission)
                            }
                          >
                            <option value="read">{t('nodesStorage.sharing.permissionRead')}</option>
                            <option value="read_write">
                              {t('nodesStorage.sharing.permissionReadWrite')}
                            </option>
                          </Select>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            disabled={creatingShare}
                            onClick={() => void onCreatePublicShare()}
                          >
                            {t('nodesStorage.sharing.createForCurrentFolder')}
                          </Button>
                        </Flex>
                        <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray8}>
                          {t('nodesStorage.sharing.currentFolderLabel', {
                            path: folderPath || t('nodesStorage.sharing.rootLicense')
                          })}
                        </Text>
                        {(shareList ?? []).filter((s) => !s.revokedAt).length === 0 ? (
                          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                            {t('nodesStorage.sharing.noActiveLinks')}
                          </Text>
                        ) : (
                          (shareList ?? [])
                            .filter((s) => !s.revokedAt)
                            .map((s) => (
                              <ShareRow key={s.shareId}>
                                <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6} style={{ flex: '1 1 140px' }}>
                                  {s.folderPath || t('nodesStorage.sharing.rootLicense')}
                                </Text>
                                <Select
                                  value={s.permission}
                                  onChange={(e) =>
                                    void onChangeSharePermission(
                                      s.shareId,
                                      e.target.value as NodeStorageSharePermission
                                    )
                                  }
                                >
                                  <option value="read">{t('nodesStorage.sharing.permissionRead')}</option>
                                  <option value="read_write">
                                    {t('nodesStorage.sharing.permissionReadWrite')}
                                  </option>
                                </Select>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => void onRotateShare(s.shareId)}
                                >
                                  {t('nodesStorage.sharing.newToken')}
                                </Button>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => void onRevokeShare(s.shareId)}
                                >
                                  {t('nodesStorage.sharing.revoke')}
                                </Button>
                              </ShareRow>
                            ))
                        )}
                      </ShareSection>
                    ) : null}

                    {listLoading ? (
                      <Text variant={TextVariant.BODY_DEFAULT}>{t('nodesStorage.loadingFiles')}</Text>
                    ) : (
                      <Flex flexDirection="column" gap="6px">
                        <ListHeaderGrid>
                          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                            {t('nodesStorage.name')}
                          </Text>
                          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                            {t('nodesStorage.modified')}
                          </Text>
                          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                            {t('nodesStorage.size')}
                          </Text>
                          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6} style={{ textAlign: 'right' }}>
                            {t('nodesStorage.actions')}
                          </Text>
                        </ListHeaderGrid>
                        {(listing?.entries ?? [])
                          .slice()
                          .sort((a, b) => {
                            if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1
                            return a.name.localeCompare(b.name)
                          })
                          .map((ent) => {
                            const rel = folderPath ? joinPath(folderPath, ent.name) : ent.name
                            return (
                              <RowGrid key={rel}>
                                <Flex
                                  gap="10px"
                                  alignItems="center"
                                  minWidth={0}
                                  style={{ cursor: ent.isDirectory ? 'pointer' : undefined }}
                                  onClick={() => ent.isDirectory && setFolderPath(rel)}
                                >
                                  {ent.isDirectory ? (
                                    <Folder size={18} color={colors.gray6} />
                                  ) : (
                                    <File size={18} color={colors.gray6} />
                                  )}
                                  <Text variant={TextVariant.BODY_DEFAULT_BOLD} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {ent.name}
                                  </Text>
                                </Flex>
                                <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                                  {new Date(ent.mtimeMs).toLocaleString()}
                                </Text>
                                <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                                  {!ent.isDirectory && ent.size != null ? formatBytes(ent.size) : '—'}
                                </Text>
                                <Flex gap="6px" justifyContent="flex-end" flexWrap="wrap">
                                  {ent.isDirectory ? (
                                    <Button variant="secondary" size="sm" onClick={() => setFolderPath(rel)}>
                                      {t('general.details')}
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => void onDownload(rel, ent.name)}
                                    >
                                      {t('general.download')}
                                    </Button>
                                  )}
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={moveBusy}
                                    onClick={() => void onRename(ent.name)}
                                  >
                                    {t('nodesStorage.rename')}
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={deleting}
                                    onClick={() => void onDelete(rel)}
                                  >
                                    {t('general.delete')}
                                  </Button>
                                </Flex>
                              </RowGrid>
                            )
                          })}
                        {(listing?.entries?.length ?? 0) === 0 ? (
                          <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                            {t('nodesStorage.emptyFolder')}
                          </Text>
                        ) : null}
                      </Flex>
                    )}
                  </DropSurface>
                )}
              </MainPanel>
            </Workspace>
          )}
        </TableShell>
      </Flex>
    </MainWrapper>
  )
}

export default NodesStorage
